export type PointStatus = 'in_range' | 'above' | 'below'

export type ChartPoint = {
  x: string
  // Numeric axis position (e.g. minutes since 5 AM for a time-of-day axis). When present it drives
  // positioning; `x` is used only as the tick label. Absent on categorical/real-time charts.
  x_value?: number
  y: number
  status?: PointStatus
}

export type ChartSeries = {
  label: string
  color?: string
  points: ChartPoint[]
}

export type ChartAnnotation = {
  // `peak`/`low` render a value-callout pill; `peak_marker` renders a plain dot (no callout) at its
  // own (x, y) — used for the recurring-spike stacked peak dots.
  kind: 'peak' | 'low' | 'peak_marker'
  x: string
  x_value?: number
  y: number
}

// Header stat shown above the chart, e.g. { label: "Average peak", value: "158 mg/dL" }.
export type ChartStat = {
  label: string
  value: string
}

// Time-axis event markers (icons drawn under the line at a point in time), distinct from
// annotations (value callouts pinned to the line). `kind` is kept as a raw string so unknown kinds
// are preserved and no-oped by the renderer (forward-compat, e.g. an upcoming 'meal' marker).
export type ChartEvent = {
  kind: string
  x: string
  icon?: string
  image_url?: string
}

export type ChartInterpolation = 'smooth'

export type ChartBlockData = {
  chart_type: 'line' | 'bar' | 'pie'
  title?: string
  x_label?: string
  y_label?: string
  threshold_low?: number
  threshold_high?: number
  interpolation?: ChartInterpolation
  show_data_points?: boolean
  hide_y_axis?: boolean
  // Whether to color out-of-range (above/below) line segments red. Defaults to true when absent.
  highlight_out_of_range?: boolean
  // Whether to show the series legend. Defaults to showing it for multi-series charts when absent.
  show_legend?: boolean
  series: ChartSeries[]
  annotations?: ChartAnnotation[]
  events?: ChartEvent[]
  stats?: ChartStat[]
}

export type MetricCardBlockData = {
  label: string
  value: string
  trend: 'up' | 'down' | 'neutral'
  delta: string
  period: string
}

export type TableBlockData = {
  title: string
  headers: string[]
  rows: string[][]
}

export type SingleChoiceOption = {
  label: string
  value: string
}

export type SingleChoiceBlockData = {
  title?: string
  field_key?: string
  options: SingleChoiceOption[]
}

export type MultiChoiceOption = SingleChoiceOption

export type MultiChoiceBlockData = {
  title?: string
  field_key?: string
  options: MultiChoiceOption[]
}

export type DateTimePickerBlockData = {
  title: string
  field_key?: string
}

export type DateTimeDurationPickerBlockData = {
  title: string
  field_key?: string
  start_field_key?: string
  end_field_key?: string
}

export type DateRangeBlockData = {
  title: string
  start_field_key: string
  end_field_key: string
}

export type CalendarBlockData = {
  title: string
  field_key: string
}

export type CalendarSlotsBlockData = {
  title: string
  field_key: string
}

export type JournalRatingOption = {
  label: string
  value: number
}

export type JournalRatingBlockData = {
  title: string
  field_key?: string
  range: {
    min: 1
    max: 5
  }
  options: JournalRatingOption[]
}

export type RangeBlockData = {
  title: string
  field_key: string
  min: number
  max: number
  metric_label: string
  increment: number
  markers: [number, number, number]
}

export type NumberStepperBlockData = {
  text: string
  field_key: string
  min: number
  max: number
  metric_label: string
  increment: number
}

export type InfoStyle = 'neutral' | 'success' | 'warning' | 'danger'

export type InfoBlockData = {
  text: string
  style: InfoStyle
}

export type SectionBlockData = {
  key: string
  title?: string
  icon?: string
  inline?: boolean
  highlight?: boolean
}

// Tone drives the value color of a comparison side. Unknown tones fall back to `neutral`.
export type ComparisonTone = 'danger' | 'success' | 'neutral'

export type ComparisonSide = {
  value: string
  label: string
  tone: ComparisonTone
}

// A "before → after" card: two values side by side with an arrow between them. `title` is optional —
// the comparison still renders without a header when it's absent.
export type ComparisonBlockData = {
  title?: string
  before: ComparisonSide
  after: ComparisonSide
}

export type NoraActionCardData = {
  title: string
  bullet_points: string[]
  button_label: string
}

export type NoraActionsBlockData = NoraActionCardData[]

export type BlocksV1Block =
  | {
      id: string
      kind: 'rich_text'
      status: 'partial' | 'complete'
      data: {
        format: 'markdown'
        text: string
      }
    }
  | {
      id: string
      kind: 'chart'
      status: 'partial' | 'complete'
      data: ChartBlockData | null
    }
  | {
      id: string
      kind: 'metric_card'
      status: 'partial' | 'complete'
      data: MetricCardBlockData | null
    }
  | {
      id: string
      kind: 'table'
      status: 'partial' | 'complete'
      data: TableBlockData | null
    }
  | {
      id: string
      kind: 'single_choice'
      status: 'partial' | 'complete'
      data: SingleChoiceBlockData | null
    }
  | {
      id: string
      kind: 'multi_choice'
      status: 'partial' | 'complete'
      data: MultiChoiceBlockData | null
    }
  | {
      id: string
      kind: 'datetime_picker'
      status: 'partial' | 'complete'
      data: DateTimePickerBlockData | null
    }
  | {
      id: string
      kind: 'datetime_duration_picker'
      status: 'partial' | 'complete'
      data: DateTimeDurationPickerBlockData | null
    }
  | {
      id: string
      kind: 'date_range_picker'
      status: 'partial' | 'complete'
      data: DateRangeBlockData | null
    }
  | {
      id: string
      kind: 'calendar'
      status: 'partial' | 'complete'
      data: CalendarBlockData | null
    }
  | {
      id: string
      kind: 'calendar_slots'
      status: 'partial' | 'complete'
      data: CalendarSlotsBlockData | null
    }
  | {
      id: string
      kind: 'journal_rating'
      status: 'partial' | 'complete'
      data: JournalRatingBlockData | null
    }
  | {
      id: string
      kind: 'range'
      status: 'partial' | 'complete'
      data: RangeBlockData | null
    }
  | {
      id: string
      kind: 'number_stepper'
      status: 'partial' | 'complete'
      data: NumberStepperBlockData | null
    }
  | {
      id: string
      kind: 'divider'
      status: 'complete'
      data: null
    }
  | {
      id: string
      kind: 'section'
      status: 'complete'
      data: SectionBlockData
    }
  | {
      id: string
      kind: 'info'
      status: 'complete'
      data: InfoBlockData
    }
  | {
      id: string
      kind: 'comparison'
      status: 'complete'
      data: ComparisonBlockData
    }
  | {
      id: string
      kind: 'nora_actions'
      status: 'complete'
      data: NoraActionsBlockData
    }

export type BlocksV1Message = {
  render_mode?: 'blocks_v1'
  render_status?: 'partial' | 'complete'
  response_id?: string
  part_index?: number
  part_count?: number
  blocks?: BlocksV1Block[]
  text?: string
  streaming?: boolean
}

import { isObject } from './draftParsing'

const parseBlockStatus = (value: unknown): 'partial' | 'complete' | undefined =>
  value === 'partial' || value === 'complete' ? value : undefined

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

const isStringArrayArray = (value: unknown): value is string[][] =>
  Array.isArray(value) && value.every(isStringArray)

const parsePointStatus = (value: unknown): PointStatus | undefined => {
  if (value === 'in_range' || value === 'above' || value === 'below') {
    return value
  }
  return undefined
}

const parseChartAnnotation = (value: unknown): ChartAnnotation | null => {
  if (!isObject(value)) {
    return null
  }
  if (value.kind !== 'peak' && value.kind !== 'low' && value.kind !== 'peak_marker') {
    return null
  }
  if (typeof value.x !== 'string' || typeof value.y !== 'number') {
    return null
  }
  return {
    kind: value.kind,
    x: value.x,
    x_value: typeof value.x_value === 'number' ? value.x_value : undefined,
    y: value.y,
  }
}

const parseChartStat = (value: unknown): ChartStat | null => {
  if (!isObject(value) || typeof value.label !== 'string' || typeof value.value !== 'string') {
    return null
  }
  return { label: value.label, value: value.value }
}

const parseChartEvent = (value: unknown): ChartEvent | null => {
  if (!isObject(value)) {
    return null
  }
  // Keep any string `kind` (unknown kinds are no-oped by the renderer, not dropped here) as long as
  // it carries an `x` to place it on the axis.
  if (typeof value.kind !== 'string' || typeof value.x !== 'string') {
    return null
  }
  return {
    kind: value.kind,
    x: value.x,
    icon: typeof value.icon === 'string' ? value.icon : undefined,
    image_url: typeof value.image_url === 'string' ? value.image_url : undefined,
  }
}

const parseChartBlockData = (data: unknown): ChartBlockData | null => {
  if (!isObject(data)) {
    return null
  }

  const chartType = data.chart_type
  if (chartType !== 'line' && chartType !== 'bar' && chartType !== 'pie') {
    return null
  }

  if (!Array.isArray(data.series)) {
    return null
  }

  const series = data.series
    .map((s): ChartSeries | null => {
      if (
        !isObject(s) ||
        typeof s.label !== 'string' ||
        !Array.isArray(s.points) ||
        s.points.length === 0
      ) {
        return null
      }
      if (
        !s.points.every((p) => isObject(p) && typeof p.x === 'string' && typeof p.y === 'number')
      ) {
        return null
      }
      return {
        label: s.label,
        color: typeof s.color === 'string' ? s.color : undefined,
        points: s.points.map((p) => {
          const point = p as Record<string, unknown>
          return {
            x: point.x as string,
            x_value: typeof point.x_value === 'number' ? point.x_value : undefined,
            y: point.y as number,
            status: parsePointStatus(point.status),
          }
        }),
      }
    })
    .filter((s): s is ChartSeries => s !== null)

  if (series.length === 0) {
    return null
  }

  const annotations = Array.isArray(data.annotations)
    ? data.annotations.map(parseChartAnnotation).filter((a): a is ChartAnnotation => a !== null)
    : undefined

  const events = Array.isArray(data.events)
    ? data.events.map(parseChartEvent).filter((e): e is ChartEvent => e !== null)
    : undefined

  const stats = Array.isArray(data.stats)
    ? data.stats.map(parseChartStat).filter((s): s is ChartStat => s !== null)
    : undefined

  return {
    chart_type: chartType,
    title: typeof data.title === 'string' ? data.title : undefined,
    x_label: typeof data.x_label === 'string' ? data.x_label : undefined,
    y_label: typeof data.y_label === 'string' ? data.y_label : undefined,
    threshold_low: typeof data.threshold_low === 'number' ? data.threshold_low : undefined,
    threshold_high: typeof data.threshold_high === 'number' ? data.threshold_high : undefined,
    interpolation: data.interpolation === 'smooth' ? 'smooth' : undefined,
    show_data_points:
      typeof data.show_data_points === 'boolean' ? data.show_data_points : undefined,
    hide_y_axis: typeof data.hide_y_axis === 'boolean' ? data.hide_y_axis : undefined,
    highlight_out_of_range:
      typeof data.highlight_out_of_range === 'boolean' ? data.highlight_out_of_range : undefined,
    show_legend: typeof data.show_legend === 'boolean' ? data.show_legend : undefined,
    series,
    annotations: annotations && annotations.length > 0 ? annotations : undefined,
    events: events && events.length > 0 ? events : undefined,
    stats: stats && stats.length > 0 ? stats : undefined,
  }
}

const parseMetricCardBlockData = (data: unknown): MetricCardBlockData | null => {
  if (!isObject(data)) {
    return null
  }

  if (typeof data.label !== 'string' || typeof data.value !== 'string') {
    return null
  }

  const trend = data.trend
  if (trend !== 'up' && trend !== 'down' && trend !== 'neutral') {
    return null
  }

  if (typeof data.delta !== 'string' || typeof data.period !== 'string') {
    return null
  }

  return {
    label: data.label,
    value: data.value,
    trend,
    delta: data.delta,
    period: data.period,
  }
}

const parseTableBlockData = (data: unknown): TableBlockData | null => {
  if (!isObject(data)) {
    return null
  }

  if (typeof data.title !== 'string') {
    return null
  }

  if (!isStringArray(data.headers) || data.headers.length === 0) {
    return null
  }

  if (!isStringArrayArray(data.rows)) {
    return null
  }

  // Normalize rows to match header count — truncate extra cells, pad missing ones
  const colCount = data.headers.length
  const rows = data.rows.map((row) => {
    if (row.length === colCount) {
      return row
    }
    if (row.length > colCount) {
      return row.slice(0, colCount)
    }
    return [...row, ...Array<string>(colCount - row.length).fill('')]
  })

  return {
    title: data.title,
    headers: data.headers,
    rows,
  }
}

const parseSingleChoiceBlockData = (data: unknown): SingleChoiceBlockData | null => {
  if (!isObject(data) || !Array.isArray(data.options)) {
    return null
  }

  const options = data.options
    .map((option) => {
      if (typeof option === 'string') {
        const trimmed = option.trim()
        if (!trimmed) {
          return null
        }
        return {
          label: trimmed,
          value: trimmed,
        }
      }

      if (
        !isObject(option) ||
        typeof option.label !== 'string' ||
        typeof option.value !== 'string'
      ) {
        return null
      }

      const label = option.label.trim()
      const value = option.value.trim()
      if (!label || !value) {
        return null
      }

      return { label, value }
    })
    .filter((option): option is SingleChoiceOption => option !== null)

  if (options.length === 0) {
    return null
  }

  return {
    title: typeof data.title === 'string' ? data.title.trim() || undefined : undefined,
    field_key: typeof data.field_key === 'string' ? data.field_key.trim() || undefined : undefined,
    options,
  }
}

const parseMultiChoiceBlockData = (data: unknown): MultiChoiceBlockData | null =>
  parseSingleChoiceBlockData(data)

const parseDateTimePickerBlockData = (data: unknown): DateTimePickerBlockData | null => {
  if (!isObject(data) || typeof data.title !== 'string') {
    return null
  }

  const title = data.title.trim()
  if (!title) {
    return null
  }

  return {
    title,
    field_key: typeof data.field_key === 'string' ? data.field_key.trim() || undefined : undefined,
  }
}

const parseDateTimeDurationPickerBlockData = (
  data: unknown,
): DateTimeDurationPickerBlockData | null => {
  if (!isObject(data) || typeof data.title !== 'string') {
    return null
  }

  const title = data.title.trim()
  if (!title) {
    return null
  }

  return {
    title,
    field_key: typeof data.field_key === 'string' ? data.field_key.trim() || undefined : undefined,
    start_field_key:
      typeof data.start_field_key === 'string'
        ? data.start_field_key.trim() || undefined
        : undefined,
    end_field_key:
      typeof data.end_field_key === 'string' ? data.end_field_key.trim() || undefined : undefined,
  }
}

const parseDateRangeBlockData = (data: unknown): DateRangeBlockData | null => {
  if (!isObject(data) || typeof data.title !== 'string') {
    return null
  }

  const title = data.title.trim()
  const startFieldKey = typeof data.start_field_key === 'string' ? data.start_field_key.trim() : ''
  const endFieldKey = typeof data.end_field_key === 'string' ? data.end_field_key.trim() : ''

  if (!title || !startFieldKey || !endFieldKey) {
    return null
  }

  return {
    title,
    start_field_key: startFieldKey,
    end_field_key: endFieldKey,
  }
}

const parseCalendarBlockData = (data: unknown): CalendarBlockData | null => {
  if (!isObject(data) || typeof data.title !== 'string' || typeof data.field_key !== 'string') {
    return null
  }

  const title = data.title.trim()
  const fieldKey = data.field_key.trim()

  if (!title || !fieldKey) {
    return null
  }

  return { title, field_key: fieldKey }
}

const parseCalendarSlotsBlockData = (data: unknown): CalendarSlotsBlockData | null =>
  parseCalendarBlockData(data)

const parseJournalRatingBlockData = (data: unknown): JournalRatingBlockData | null => {
  if (!isObject(data) || typeof data.title !== 'string' || !isObject(data.range)) {
    return null
  }

  const title = data.title.trim()
  const rangeMin = data.range.min
  const rangeMax = data.range.max
  if (!title || rangeMin !== 1 || rangeMax !== 5 || !Array.isArray(data.options)) {
    return null
  }

  const options = data.options
    .map((option) => {
      if (
        !isObject(option) ||
        typeof option.label !== 'string' ||
        typeof option.value !== 'number'
      ) {
        return null
      }

      const label = option.label.trim()
      if (!label || option.value < rangeMin || option.value > rangeMax) {
        return null
      }

      return { label, value: option.value }
    })
    .filter((option): option is JournalRatingOption => option !== null)

  if (options.length !== 5) {
    return null
  }

  return {
    title,
    field_key: typeof data.field_key === 'string' ? data.field_key.trim() || undefined : undefined,
    range: { min: 1, max: 5 },
    options,
  }
}

const parseRangeBlockData = (data: unknown): RangeBlockData | null => {
  if (!isObject(data)) {
    return null
  }

  if (
    typeof data.title !== 'string' ||
    typeof data.field_key !== 'string' ||
    typeof data.min !== 'number' ||
    typeof data.max !== 'number' ||
    typeof data.metric_label !== 'string' ||
    typeof data.increment !== 'number'
  ) {
    return null
  }

  const title = data.title.trim()
  const fieldKey = data.field_key.trim()
  const metricLabel = data.metric_label.trim()

  if (!title || !fieldKey || !metricLabel) {
    return null
  }

  if (
    !Number.isFinite(data.min) ||
    !Number.isFinite(data.max) ||
    !Number.isFinite(data.increment) ||
    data.min >= data.max ||
    data.increment <= 0
  ) {
    return null
  }

  if (
    !Array.isArray(data.markers) ||
    data.markers.length !== 3 ||
    !data.markers.every((marker) => Number.isFinite(marker))
  ) {
    return null
  }

  const markers = data.markers as [number, number, number]
  const [firstMarker, middleMarker, lastMarker] = markers

  if (
    firstMarker < data.min ||
    middleMarker < data.min ||
    lastMarker < data.min ||
    firstMarker > data.max ||
    middleMarker > data.max ||
    lastMarker > data.max
  ) {
    return null
  }

  return {
    title,
    field_key: fieldKey,
    min: data.min,
    max: data.max,
    metric_label: metricLabel,
    increment: data.increment,
    markers,
  }
}

const parseNumberStepperBlockData = (data: unknown): NumberStepperBlockData | null => {
  if (!isObject(data)) {
    return null
  }

  if (
    typeof data.text !== 'string' ||
    typeof data.field_key !== 'string' ||
    typeof data.min !== 'number' ||
    typeof data.max !== 'number' ||
    typeof data.metric_label !== 'string' ||
    typeof data.increment !== 'number'
  ) {
    return null
  }

  const text = data.text.trim()
  const fieldKey = data.field_key.trim()
  const metricLabel = data.metric_label.trim()

  if (!text || !fieldKey || !metricLabel) {
    return null
  }

  if (
    !Number.isFinite(data.min) ||
    !Number.isFinite(data.max) ||
    !Number.isFinite(data.increment) ||
    data.min >= data.max ||
    data.increment <= 0
  ) {
    return null
  }

  return {
    text,
    field_key: fieldKey,
    min: data.min,
    max: data.max,
    metric_label: metricLabel,
    increment: data.increment,
  }
}

const parseInfoStyle = (value: unknown): InfoStyle | undefined => {
  if (value === 'neutral' || value === 'success' || value === 'warning' || value === 'danger') {
    return value
  }
  return undefined
}

const parseInfoBlockData = (data: unknown): InfoBlockData | null => {
  if (!isObject(data)) {
    return null
  }
  if (typeof data.text !== 'string' || !data.text.trim()) {
    return null
  }
  const style = parseInfoStyle(data.style)
  if (!style) {
    return null
  }
  return { text: data.text, style }
}

const parseComparisonTone = (value: unknown): ComparisonTone =>
  value === 'danger' || value === 'success' ? value : 'neutral'

const parseComparisonSide = (value: unknown): ComparisonSide | null => {
  if (!isObject(value) || typeof value.value !== 'string' || typeof value.label !== 'string') {
    return null
  }
  // Trim so the renderer gets normalized, displayable data (e.g. a leading space would otherwise make
  // the split "amount" empty); a side with no value or label isn't renderable, so drop it.
  const displayValue = value.value.trim()
  const label = value.label.trim()
  if (!displayValue || !label) {
    return null
  }
  return { value: displayValue, label, tone: parseComparisonTone(value.tone) }
}

const parseComparisonBlockData = (data: unknown): ComparisonBlockData | null => {
  if (!isObject(data)) {
    return null
  }
  // The two sides are the block's substance; without them there's nothing to show. The title is an
  // optional header — a blank/missing one just renders the comparison without it.
  const before = parseComparisonSide(data.before)
  const after = parseComparisonSide(data.after)
  if (!before || !after) {
    return null
  }
  const title = typeof data.title === 'string' ? data.title.trim() : ''
  return { title: title || undefined, before, after }
}

const parseNoraActionCardData = (value: unknown): NoraActionCardData | null => {
  if (
    !isObject(value) ||
    typeof value.title !== 'string' ||
    !Array.isArray(value.bullet_points) ||
    typeof value.button_label !== 'string'
  ) {
    return null
  }

  const title = value.title.trim()
  const buttonLabel = value.button_label.trim()
  const bulletPoints = value.bullet_points
    .filter((bulletPoint): bulletPoint is string => typeof bulletPoint === 'string')
    .map((bulletPoint) => bulletPoint.trim())
    .filter(Boolean)

  if (!title || !buttonLabel || bulletPoints.length === 0) {
    return null
  }

  return {
    title,
    bullet_points: bulletPoints,
    button_label: buttonLabel,
  }
}

const parseNoraActionsBlockData = (data: unknown): NoraActionsBlockData | null => {
  const items = isObject(data) && Array.isArray(data.items) ? data.items : data

  if (!Array.isArray(items)) {
    return null
  }

  const actions = items
    .map(parseNoraActionCardData)
    .filter((action): action is NoraActionCardData => action !== null)

  return actions.length > 0 ? actions : null
}

const parseSectionBlockData = (data: unknown): SectionBlockData | null => {
  if (!isObject(data)) {
    return null
  }

  if (typeof data.key !== 'string') {
    return null
  }

  return {
    key: data.key,
    title: typeof data.title === 'string' ? data.title : undefined,
    icon: typeof data.icon === 'string' ? data.icon : undefined,
    inline: typeof data.inline === 'boolean' ? data.inline : undefined,
    highlight: typeof data.highlight === 'boolean' ? data.highlight : undefined,
  }
}

const parseBlock = (value: unknown): BlocksV1Block | null => {
  if (!isObject(value) || typeof value.id !== 'string' || typeof value.kind !== 'string') {
    return null
  }

  const status = parseBlockStatus(value.status)

  switch (value.kind) {
    case 'rich_text': {
      if (
        !isObject(value.data) ||
        value.data.format !== 'markdown' ||
        typeof value.data.text !== 'string'
      ) {
        return null
      }
      if (!status) {
        return null
      }
      return {
        id: value.id,
        kind: 'rich_text',
        status,
        data: { format: 'markdown', text: value.data.text },
      }
    }

    case 'chart': {
      if (!status) {
        return null
      }
      const chartData = status === 'complete' ? parseChartBlockData(value.data) : null
      if (status === 'complete' && !chartData) {
        return null
      }
      return { id: value.id, kind: 'chart', status, data: chartData }
    }

    case 'metric_card': {
      if (!status) {
        return null
      }
      const metricData = status === 'complete' ? parseMetricCardBlockData(value.data) : null
      if (status === 'complete' && !metricData) {
        return null
      }
      return { id: value.id, kind: 'metric_card', status, data: metricData }
    }

    case 'table': {
      if (!status) {
        return null
      }
      const tableData = status === 'complete' ? parseTableBlockData(value.data) : null
      if (status === 'complete' && !tableData) {
        return null
      }
      return { id: value.id, kind: 'table', status, data: tableData }
    }

    case 'single_choice': {
      if (!status) {
        return null
      }
      const singleChoiceData = status === 'complete' ? parseSingleChoiceBlockData(value.data) : null
      if (status === 'complete' && !singleChoiceData) {
        return null
      }
      return { id: value.id, kind: 'single_choice', status, data: singleChoiceData }
    }

    case 'multi_choice': {
      if (!status) {
        return null
      }
      const multiChoiceData = status === 'complete' ? parseMultiChoiceBlockData(value.data) : null
      if (status === 'complete' && !multiChoiceData) {
        return null
      }
      return { id: value.id, kind: 'multi_choice', status, data: multiChoiceData }
    }

    case 'datetime_picker': {
      if (!status) {
        return null
      }
      const dateTimePickerData =
        status === 'complete' ? parseDateTimePickerBlockData(value.data) : null
      if (status === 'complete' && !dateTimePickerData) {
        return null
      }
      return { id: value.id, kind: 'datetime_picker', status, data: dateTimePickerData }
    }

    case 'datetime_duration_picker': {
      if (!status) {
        return null
      }
      const dateTimeDurationPickerData =
        status === 'complete' ? parseDateTimeDurationPickerBlockData(value.data) : null
      if (status === 'complete' && !dateTimeDurationPickerData) {
        return null
      }
      return {
        id: value.id,
        kind: 'datetime_duration_picker',
        status,
        data: dateTimeDurationPickerData,
      }
    }

    case 'date_range_picker': {
      if (!status) {
        return null
      }
      const dateRangeData = status === 'complete' ? parseDateRangeBlockData(value.data) : null
      if (status === 'complete' && !dateRangeData) {
        return null
      }
      return { id: value.id, kind: 'date_range_picker', status, data: dateRangeData }
    }

    case 'calendar': {
      if (!status) {
        return null
      }
      const calendarData = status === 'complete' ? parseCalendarBlockData(value.data) : null
      if (status === 'complete' && !calendarData) {
        return null
      }
      return { id: value.id, kind: 'calendar', status, data: calendarData }
    }

    case 'calendar_slots': {
      if (!status) {
        return null
      }
      const calendarSlotsData =
        status === 'complete' ? parseCalendarSlotsBlockData(value.data) : null
      if (status === 'complete' && !calendarSlotsData) {
        return null
      }
      return { id: value.id, kind: 'calendar_slots', status, data: calendarSlotsData }
    }

    case 'journal_rating': {
      if (!status) {
        return null
      }
      const journalRatingData =
        status === 'complete' ? parseJournalRatingBlockData(value.data) : null
      if (status === 'complete' && !journalRatingData) {
        return null
      }
      return { id: value.id, kind: 'journal_rating', status, data: journalRatingData }
    }

    case 'range': {
      if (!status) {
        return null
      }
      const rangeData = status === 'complete' ? parseRangeBlockData(value.data) : null
      if (status === 'complete' && !rangeData) {
        return null
      }
      return { id: value.id, kind: 'range', status, data: rangeData }
    }

    case 'number_stepper': {
      if (!status) {
        return null
      }
      const numberStepperData =
        status === 'complete' ? parseNumberStepperBlockData(value.data) : null
      if (status === 'complete' && !numberStepperData) {
        return null
      }
      return { id: value.id, kind: 'number_stepper', status, data: numberStepperData }
    }

    case 'divider': {
      return {
        id: value.id,
        kind: 'divider',
        status: 'complete',
        data: null,
      }
    }

    case 'info': {
      const infoData = parseInfoBlockData(value.data)
      if (!infoData) {
        return null
      }
      return {
        id: value.id,
        kind: 'info',
        status: 'complete',
        data: infoData,
      }
    }

    case 'section': {
      const sectionData = parseSectionBlockData(value.data)
      if (!sectionData) {
        return null
      }
      return {
        id: value.id,
        kind: 'section',
        status: 'complete',
        data: sectionData,
      }
    }

    case 'comparison': {
      const comparisonData = parseComparisonBlockData(value.data)
      if (!comparisonData) {
        return null
      }
      return {
        id: value.id,
        kind: 'comparison',
        status: 'complete',
        data: comparisonData,
      }
    }

    case 'nora_actions': {
      const noraActionsData = parseNoraActionsBlockData(value.data)
      if (!noraActionsData) {
        return null
      }
      return {
        id: value.id,
        kind: 'nora_actions',
        status: 'complete',
        data: noraActionsData,
      }
    }

    default:
      return null
  }
}

export const parseBlocksV1Message = (value: unknown): BlocksV1Message | null => {
  if (!isObject(value) || value.render_mode !== 'blocks_v1') {
    return null
  }

  const blocks = Array.isArray(value.blocks)
    ? value.blocks.map(parseBlock).filter((block): block is BlocksV1Block => block !== null)
    : undefined

  return {
    render_mode: 'blocks_v1',
    render_status: parseBlockStatus(value.render_status),
    response_id: typeof value.response_id === 'string' ? value.response_id : undefined,
    part_index: typeof value.part_index === 'number' ? value.part_index : undefined,
    part_count: typeof value.part_count === 'number' ? value.part_count : undefined,
    blocks,
    text: typeof value.text === 'string' ? value.text : undefined,
    streaming: typeof value.streaming === 'boolean' ? value.streaming : undefined,
  }
}

export const hasRenderableBlocksV1Content = (message: BlocksV1Message) =>
  Array.isArray(message.blocks) && message.blocks.length > 0

export const isBlocksV1Partial = (message: BlocksV1Message) =>
  message.streaming === true ||
  message.render_status === 'partial' ||
  message.blocks?.some((block) => block.status === 'partial') === true
